import { DobPipe } from '../../pipes/dob.pipe';
import { MedBadgesComponent } from '../../med-badges/med-badges.component';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BatchPickService } from '../batchpick.service';
import { BasketDetail, BatchPickState, MedicationDetail, isMedPicked } from '../models/batchpick-state';
import { IssueBannerComponent, IssueBannerInput } from './issue-banner.component';
import { Subscription } from 'rxjs';
import { BatchService } from '../../Services/batch.service';
import { NsTrayPrintService } from '../../Services/ns-tray-print.service';
import { BagProgress, MpsBatchForLabels, bagProgress } from '../../Services/mps-label-builder';
import { VerifyFinishService } from './verify-finish.service';
import { CLOSED_STATUSES, LOCKED_STATUSES } from '../../models/batch-status';
import { BagReprintPanelComponent } from '../../bag-reprint-panel/bag-reprint-panel.component';

@Component({
  selector: 'app-batchpick-verify-baskets',
  standalone: true,
  imports: [DobPipe, CommonModule, FormsModule, IssueBannerComponent, BagReprintPanelComponent, MedBadgesComponent],
  templateUrl: './verify-baskets.component.html',
  styleUrl: './verify-baskets.component.css',
})
export class VerifyBasketsComponent implements OnDestroy {
  @ViewChild('scanInput') scanInput!: ElementRef<HTMLInputElement>;

  batchId!: string;
  state?: BatchPickState;
  barcode = '';
  currentBasket: number | null = null;
  pulseBasket: number | null = null;
  banner: { type: 'success' | 'warn' | 'error'; text: string } | null = null;
  issue: IssueBannerInput | null = null;
  /** Batch detail (bags, patients) for the Reprint bag labels dialog; null while it's closed. */
  bagReprintBatch: MpsBatchForLabels | null = null;
  /** Per MPS bag: lines verified out of lines in the bag. */
  bagStatus: BagProgress[] = [];
  finishing = false;
  /** Finished on the server, with a bag-label problem shown: Finish now just leaves. */
  finished = false;
  private traySub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: BatchPickService,
    private batches: BatchService,
    private tray: NsTrayPrintService,
    private finisher: VerifyFinishService,
  ) {}

  ngOnInit() {
    this.batchId = this.route.snapshot.paramMap.get('batchId')!;
    this.bootstrap();
  }

  ngOnDestroy() {
    this.traySub?.unsubscribe();
  }

  /** Finished, refused or cancelled: the screen only shows it (and reprints its bag labels). */
  get readOnly(): boolean {
    return CLOSED_STATUSES.includes(this.state?.status ?? '');
  }

  /** Sent by MPS: finishing prints a bag label for every fully verified bag. */
  get isMpsBatch(): boolean {
    return this.state?.source === 'Mps';
  }

  /** Reprint is offered on MPS batches, except refused and cancelled ones (MPS replaces their barcodes). */
  get canReprintBags(): boolean {
    return this.isMpsBatch && !LOCKED_STATUSES.includes(this.state?.status ?? '');
  }

  /** Only MPS batches print here, so only they need NS Tray (and its alerts in the banner). */
  private connectTray() {
    if (this.traySub) return;
    this.tray.connect();
    this.traySub = this.tray.alerts$.subscribe(a =>
      (this.banner = { type: a.type === 'warning' ? 'warn' : a.type, text: a.message }));
  }

  private bootstrap() {
    this.api.state(this.batchId).subscribe(s => {
      if (s.status === 'Picked') {
        this.api.startVerify(this.batchId).subscribe(() => this.refresh());
      } else {
        this.applyState(s);
      }
    });
  }

  refresh() {
    this.api.state(this.batchId).subscribe(s => this.applyState(s));
  }

  private applyState(s: BatchPickState) {
    this.state = s;
    if (this.isMpsBatch) this.connectTray();
    this.bagStatus = this.computeBagStatus(s);
    if (this.currentBasket === null || this.basketComplete(s.baskets.find(b => b.basketNumber === this.currentBasket)!)) {
      this.currentBasket = this.firstIncompleteBasket()?.basketNumber ?? s.baskets[0]?.basketNumber ?? null;
    }
    queueMicrotask(() => this.scanInput?.nativeElement.focus());
  }

  private firstIncompleteBasket(): BasketDetail | undefined {
    return this.state?.baskets.find(b => !this.basketComplete(b));
  }

  scan() {
    const code = this.barcode.trim();
    if (!code) return;
    this.barcode = '';
    const basket = this.currentBasket ?? this.state?.baskets[0]?.basketNumber;
    if (basket == null) return;
    this.api.verifyScan(this.batchId, basket, code).subscribe({
      next: r => {
        if (r.kind === 'Verified') {
          this.issue = null;
          this.currentBasket = basket;
          this.triggerPulse(basket);
          this.banner = { type: 'success', text: `Basket #${basket} — verified (${r.basketFill})${r.isSubstitute ? ' · ALT' : ''}` };
          this.refresh();
        } else if (r.kind === 'AlreadyVerified') {
          this.issue = null;
          this.banner = { type: 'warn', text: 'Already verified.' };
        } else if (r.kind === 'WrongBasket') {
          const targetPatient = this.state?.baskets.find(b => b.basketNumber === r.belongsInBasket)?.patientName;
          this.issue = {
            kind: 'WrongBasket',
            medicationId: r.medicationId,
            medicationName: this.findMedName(r.medicationId),
            scannedProductId: code,
            expectedBasket: r.belongsInBasket,
            expectedPatient: targetPatient,
          };
          this.banner = null;
        } else if (r.kind === 'OrphanStock') {
          this.issue = { kind: 'OrphanStock', scannedProductId: code };
          this.banner = null;
        }
        // Keep the handheld scanner focused after every result (matches single-basket view).
        queueMicrotask(() => this.scanInput?.nativeElement.focus());
      },
      error: () => {
        this.banner = { type: 'error', text: 'Scan failed — retry.' };
        queueMicrotask(() => this.scanInput?.nativeElement.focus());
      },
    });
  }

  private findMedName(medId?: string): string | undefined {
    if (!medId || !this.state) return undefined;
    for (const b of this.state.baskets) {
      const m = b.medications.find(x => x.medicationId === medId);
      if (m) return m.medicationName;
    }
    return undefined;
  }

  moveNow() {
    if (!this.issue || this.issue.kind !== 'WrongBasket' || !this.issue.medicationId || this.issue.expectedBasket == null) return;
    const scannedIn = this.currentBasket ?? this.issue.expectedBasket;
    const target = this.issue.expectedBasket;
    this.api.moveNow(this.batchId, scannedIn, this.issue.medicationId, scannedIn, target).subscribe(() => {
      this.issue = null;
      this.banner = { type: 'success', text: `Moved to basket #${target}.` };
      this.refresh();
    });
  }

  defer() {
    if (!this.issue) return;
    this.api.deferIssue(this.batchId, {
      kind: this.issue.kind,
      medicationId: this.issue.medicationId,
      scannedProductId: this.issue.scannedProductId,
      scannedInBasket: this.issue.kind === 'WrongBasket' ? (this.currentBasket ?? undefined) : undefined,
      expectedBasket: this.issue.kind === 'WrongBasket' ? this.issue.expectedBasket : undefined,
    }).subscribe(() => {
      this.issue = null;
      this.banner = { type: 'warn', text: 'Deferred to end-of-session resolution.' };
      this.refresh();
    });
  }

  dismiss() { this.issue = null; }

  /** The basket's lines on the scan screen, to look at: this batch is scanned and finished here. */
  openBatchDetail(b?: BasketDetail) {
    this.router.navigate(['/batch', this.batchId], { queryParams: b ? { rx: b.rxId, mode: 'view' } : { mode: 'view' } });
  }

  /** Glyph state for a med on the verify grid: verified ✓ / missing ✕ / awaiting ● / pending ○. */
  markState(m: MedicationDetail): 'verified' | 'missing' | 'awaiting' | 'pending' {
    if (m.verifyScanStatus === 'Verified') return 'verified';
    if (m.pickScanStatus === 'Missing')   return 'missing';
    if (isMedPicked(m))                    return 'awaiting';
    return 'pending';
  }

  basketComplete(b: BasketDetail): boolean {
    if (!b || b.medications.length === 0) return false;
    return b.medications.every(m =>
      m.verifyScanStatus === 'Verified' || m.pickScanStatus === 'Missing');
  }

  basketHasIssue(b: BasketDetail): boolean {
    if (!this.state) return false;
    return this.state.unresolvedIssues.some(i =>
      i.scannedInBasket === b.basketNumber || i.expectedBasket === b.basketNumber);
  }

  allBasketsComplete(): boolean {
    if (!this.state) return false;
    return this.state.baskets.every(b => this.basketComplete(b)) && !this.state.hasUnresolvedIssues;
  }

  async finish(): Promise<void> {
    if (this.finished) {
      this.router.navigate(['/batchlist']);
      return;
    }
    if (!this.state || this.finishing) return;
    if (this.state.hasUnresolvedIssues) {
      this.router.navigate(['/batchpick', this.batchId, 'verify', 'issues']);
      return;
    }
    this.finishing = true;
    try {
      const labelProblem = await this.finisher.finish(this.batchId, this.state);
      if (labelProblem === null) {
        this.router.navigate(['/batchlist']);
        return;
      }
      this.finished = true;
      this.banner = { type: 'error', text: `Batch finished, but ${labelProblem}. Use Reprint bag labels.` };
      // The batch is finished now: reload it, so the screen goes read-only (no more scanning).
      this.refresh();
    } catch {
      this.banner = { type: 'error', text: 'Could not finish verifying — retry.' };
    } finally {
      this.finishing = false;
    }
  }

  private computeBagStatus(s: BatchPickState): BagProgress[] {
    if (s.source !== 'Mps') return [];
    const bagIds = [...new Set(s.baskets.flatMap(b => b.medications).map(m => m.bagId).filter((id): id is string => !!id))];
    const batch: MpsBatchForLabels = {
      prescriptions: s.baskets.map(b => ({ rxId: b.rxId, patientName: b.patientName, medications: b.medications })),
      bags: bagIds.map(bagId => ({ bagId, barcode: '' })),
    };
    const verified = new Set(s.baskets.flatMap(b => b.medications)
      .filter(m => m.verifyScanStatus === 'Verified').map(m => m.medicationId));
    return bagProgress(batch, (_rxId, medicationId) => verified.has(medicationId));
  }

  openBagReprint() {
    this.batches.getBatchById(this.batchId).subscribe((res: any) => (this.bagReprintBatch = res.result));
  }

  closeBagReprint() {
    this.bagReprintBatch = null;
    queueMicrotask(() => this.scanInput?.nativeElement.focus());
  }

  private triggerPulse(basket: number) {
    this.pulseBasket = null;
    queueMicrotask(() => {
      this.pulseBasket = basket;
      setTimeout(() => {
        if (this.pulseBasket === basket) this.pulseBasket = null;
      }, 900);
    });
  }

  trackByBasket(_: number, b: BasketDetail) { return b.basketNumber; }
  trackByMed(_: number, m: { medicationId: string }) { return m.medicationId; }
}
